import { useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiPhone,
  FiSearch,
  FiTrash2,
  FiUserPlus,
} from "react-icons/fi";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../store";
import { deleteContact, fetchContacts, selectContactPagination, selectContactSummary } from "../../../store/slices/contactSlice";
import PageWrapper from "../../common/PageWrapper";
import CreateContactModal from "../../modules/Contact/CreateContactModal";
import AddLeadModal from "../../modules/Lead/AddLeadModal";
import HeaderSection from "../../../../../components/HeaderSection";
import Pagination from "../../../../../components/common/Pagination";
import { DEFAULT_PAGINATION } from "../../../../../constants/pagination";
import "./ContactPage.css";
import { Button } from "@mui/material";
import { capitalizeWords } from '../../../../../utils/formatters';

const ContactPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { contacts, loading } = useAppSelector((state) => state.contacts);
  const pagination = useAppSelector(selectContactPagination);

  const [activeTab, setActiveTab] = useState<
    "all" | "active" | "pending" | "inactive"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGINATION.page);
  const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_PAGINATION.limit);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, selectedStatus]);

  useEffect(() => {
    const statusFilter = activeTab !== "all" ? activeTab : (selectedStatus || undefined);
    dispatch(fetchContacts({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm || undefined,
      status: statusFilter
    }));
  }, [dispatch, currentPage, itemsPerPage, searchTerm, activeTab, selectedStatus]);

  // Column headers for contact table
  const tableColumns = [
    "#",
    "Contact",
    "Phone",
    "Whatsapp",
    "Reference By",
    "City",
    "Status",
    "Actions",
  ];

  // Filter contacts based on active tab and filters
  const getFilteredContacts = () => {
    let filtered = contacts;

    // Filter by tab
    if (activeTab === "active") {
      filtered = filtered.filter((c) => c.status === "active");
    } else if (activeTab === "pending") {
      filtered = filtered.filter((c) => c.status === "pending");
    } else if (activeTab === "inactive") {
      filtered = filtered.filter((c) => c.status === "inactive");
    }

    // Filter by search term
    if (searchTerm) {
      const lowered = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) => {
          if (!c) return false;
          const name = c.name ? String(c.name).toLowerCase() : '';
          const email = c.email ? String(c.email).toLowerCase() : '';
          const phone = c.phone ? String(c.phone) : '';
          const siteName = c.siteName ? String(c.siteName).toLowerCase() : '';
          const referenceBy = c.referenceBy ? String(c.referenceBy).toLowerCase() : '';
          const city = (c as any).city ? String((c as any).city).toLowerCase() : '';

          return (
            name.includes(lowered) ||
            email.includes(lowered) ||
            phone.includes(searchTerm) ||
            siteName.includes(lowered) ||
            referenceBy.includes(lowered) ||
            city.includes(lowered)
          );
        }
      );
    }

    // Filter by status dropdown
    if (selectedStatus) {
      filtered = filtered.filter((c) => c.status === selectedStatus);
    }

    return filtered;
  };

  const filteredContacts = getFilteredContacts();

  const summary = useAppSelector(selectContactSummary);

  // Calculate counts for tabs using summary from backend
  const allCount = summary?.total || 0;
  const activeCount = summary?.active || 0;
  const pendingCount = summary?.pending || 0;
  const inactiveCount = summary?.inactive || 0;

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      dispatch(deleteContact(id));
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedStatus("");
    setActiveTab("all");
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return "#3b82f6";
      case "pending":
        return "#f59e0b";
      case "inactive":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const openWhatsAppChat = (phone: string, whatsapp?: string) => {
    const targetNumber = whatsapp || phone;
    if (!targetNumber) return;

    const cleaned = targetNumber.replace(/\D/g, "");
    if (!cleaned) return;

    const whatsappUrl = `https://wa.me/${cleaned}`;
    window.open(whatsappUrl, "_blank");
  };

  const startPhoneCall = (phone: string) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  return (
    <PageWrapper>
      <div className="contact-page">
        <HeaderSection
          title="Contacts"
          description="Manage and track all your contacts in one place."
          icon={
            <PhoneIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          }
          buttonIcon={<FiUserPlus className="h-5 w-5 mr-2" />}
          // buttonText="Add New Contact"
          // onButtonClick={() => setIsAddLeadModalOpen(true)}
        />

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            className={`tab-button ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All Contacts
            <span className="tab-count">{allCount}</span>
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active
            <span className="tab-count">{activeCount}</span>
          </button>
          {/* <button
            type="button"
            className={`tab-button ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            Pending
            <span className="tab-count">{pendingCount}</span>
          </button> */}
          {
            inactiveCount > 0 && (
              <button
                type="button"
                className={`tab-button ${activeTab === "inactive" ? "active" : ""}`}
                onClick={() => setActiveTab("inactive")}
              >
                Inactive
                <span className="tab-count">{inactiveCount}</span>
              </button>
            )
          }
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, phone or company"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            type="button"
            className="clear-filters-btn"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>

        {/* Contacts Table */}
        <div className="table-container">
          {filteredContacts.length === 0 ? (
            <div className="empty-state">No contacts found.</div>
          ) : (
            <table className="contacts-table">
              <thead>
                <tr>
                  {tableColumns.map((column, index) => (
                    <th key={index}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact, index) => (
                  <tr
                    key={contact.id}
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="id-cell">
                      {((currentPage - 1) * itemsPerPage + index + 1).toString().padStart(3, "0")}
                    </td>
                    <td className="name-cell">
                      <div className="name-with-avatar">
                        <div className="avatar-circle">
                          {getInitials(contact.name)}
                        </div>
                        <div>
                          <div className="contact-name">{capitalizeWords(contact.name)}</div>
                          <div className="contact-email-small">
                            {contact.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {/* <div>{contact.whatsapp || "-"}</div> */}
                      <div>{contact.phone}</div>
                    </td>

                    <td>
                      <div>{contact.whatsapp || "-"}</div>
                    </td>
                    <td>
                      <div>{capitalizeWords(contact.referenceBy) || "-"}</div>
                    </td>
                    <td>
                      <div>{(contact as any).city || "-"}</div>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: getStatusBadgeColor(contact.status),
                        }}
                      >
                        {contact.status}
                      </span>
                    </td>
                    <td
                      className="actions-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="text"
                        onClick={() =>
                          openWhatsAppChat(contact.phone, contact.whatsapp)
                        }
                        sx={{
                          color: '#25D366',
                          minWidth: 'auto',
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(37, 211, 102, 0.1)',
                          },
                        }}
                      >
                        <FaWhatsapp size={20} />
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => startPhoneCall(contact.phone)}
                        sx={{
                          color: '#0284c7',
                          minWidth: 'auto',
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(2, 132, 199, 0.1)',
                          },
                        }}
                      >
                        <FiPhone size={20} />
                      </Button>
                      {/* <Button
                        variant="text"
                        color="error"
                        onClick={() => handleDelete(contact.id)}
                        sx={{
                          minWidth: 'auto',
                          padding: '4px',
                        }}
                      >
                        <FiTrash2 size={20} />
                      </Button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onItemsPerPageChange={(limit) => {
              setItemsPerPage(limit);
              setCurrentPage(1);
            }}
          />
        )}

        {/* Create Contact Modal (currently unused for contact CRUD) */}
        <CreateContactModal
          isOpen={isCreateModalOpen}
          onRequestClose={() => setIsCreateModalOpen(false)}
        />

        {/* Add/Edit Contact Modal (reusing lead modal in contact mode) */}
        <AddLeadModal
          isOpen={isAddLeadModalOpen}
          onRequestClose={() => setIsAddLeadModalOpen(false)}
          mode="contact"
        />

      </div>
    </PageWrapper>
  );
};

export default ContactPage;
